import React from 'react';
import { getPagesByGroup, PermissionOverride } from '../../types/permissions';

const OPERATIONS = ['view', 'create', 'update', 'delete', 'export'] as const;
type Operation = typeof OPERATIONS[number];

interface PermissionMatrixProps {
  overrides: PermissionOverride[];
  defaults?: Record<string, Record<Operation, boolean>>;
  onChange: (page: string, operation: string, value: boolean | null) => void;
  userOverridePages?: string[];
  showReset?: boolean;
  onReset?: (page: string) => void;
}

function getOverrideValue(overrides: PermissionOverride[], page: string, op: Operation): boolean | null {
  const row = overrides.find(o => o.page === page);
  if (!row) return null;
  const key = `can_${op}` as keyof PermissionOverride;
  return row[key] as boolean | null;
}

export const PermissionMatrix: React.FC<PermissionMatrixProps> = ({
  overrides,
  defaults,
  onChange,
  userOverridePages,
  showReset,
  onReset,
}) => {
  const groups = getPagesByGroup();

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 px-4 font-semibold text-gray-700 w-48">Page</th>
            {OPERATIONS.map(op => (
              <th key={op} className="text-center py-3 px-2 font-semibold text-gray-700 capitalize w-20">
                {op}
              </th>
            ))}
            {showReset && <th className="w-20"></th>}
          </tr>
        </thead>
        <tbody>
          {groups.map(group => (
            <React.Fragment key={group.group}>
              <tr>
                <td colSpan={OPERATIONS.length + 1 + (showReset ? 1 : 0)} className="pt-4 pb-1 px-4">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                    {group.group}
                  </span>
                </td>
              </tr>
              {group.pages.map(page => {
                const hasUserOverride = userOverridePages?.includes(page.slug);
                const hasAnyOverride = overrides.some(o => o.page === page.slug);

                return (
                  <tr key={page.slug} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2.5 px-4 font-medium text-gray-800 flex items-center gap-2">
                      {page.label}
                      {hasUserOverride && (
                        <span className="w-2 h-2 rounded-full bg-emerald-500" title="User override active" />
                      )}
                    </td>
                    {OPERATIONS.map(op => {
                      const overrideVal = getOverrideValue(overrides, page.slug, op);
                      const defaultVal = defaults?.[page.slug]?.[op];

                      return (
                        <td key={op} className="text-center py-2.5 px-2">
                          <button
                            onClick={() => {
                              if (overrideVal === null) {
                                onChange(page.slug, op, !(defaultVal ?? false));
                              } else if (overrideVal === true) {
                                onChange(page.slug, op, false);
                              } else {
                                onChange(page.slug, op, null);
                              }
                            }}
                            className={`w-7 h-7 rounded border-2 flex items-center justify-center transition-colors ${
                              overrideVal === true
                                ? 'bg-green-500 border-green-500 text-white'
                                : overrideVal === false
                                ? 'bg-white border-red-300 text-red-400'
                                : 'bg-gray-100 border-gray-300 text-gray-400'
                            }`}
                            title={
                              overrideVal === true
                                ? 'Granted (click to deny)'
                                : overrideVal === false
                                ? 'Denied (click to clear override)'
                                : `Default: ${defaultVal ? 'allowed' : 'denied'} (click to override)`
                            }
                          >
                            {overrideVal === true ? (
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : overrideVal === false ? (
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            ) : (
                              <span className="text-xs font-bold">&mdash;</span>
                            )}
                          </button>
                        </td>
                      );
                    })}
                    {showReset && (
                      <td className="text-center py-2.5 px-2">
                        {hasAnyOverride && (
                          <button
                            onClick={() => onReset?.(page.slug)}
                            className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                            title="Reset to defaults"
                          >
                            Reset
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};
